import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class Validator {

}

// validator for general values
export function generalValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return !!control.value && control.valid ? null : { invalid: true };
  }
}

// choosing a species from a species list
export function speciesValidator(speciesList: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    for (let species of speciesList) {
      if (control.value.toLowerCase() === species.toLowerCase()) {
        return null;
      }
    }
    return { value: { error: 'Invalid species' } };
  }
}


/**
 * Detect if coordinate fields are empty.
 * @returns Validator
 */
export function coordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
      return control.value && control.value.decimal ? null : {object: {error: 'Empty value'}};
  }
}


/**
 * If latitude has been entered manually, it is subject to constraints. Must be greater than 31.35 and less than 49.
 * @param manual Has the control value been entered manually?
 * @returns Validator function
 */
export function latitudeValidator(min: number = 38, max: number = 72): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    let coord = control.value.degrees + control.value.minutes/100;
    return coord < min || coord > max ? { latitude: { error: 'Latitude out of range' } } : null;
  }
}

/**
* If longitude has been entered manually, it is subject to constraints. Must be greater than 95.05 and less than 134.416.
* @param manual Has the control value been entered manually?
* @returns Validator function
*/
export function longitudeValidator(min: number = 40, max: number = 148): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    let coord = control.value.degrees + control.value.minutes/100;
    return coord < min || coord > max ? { longitude: { error: 'Longitude out of range' } } : null;
  }
}

/**
 * Strips a raw input string down to a well-formed decimal number: removes non-digits, keeps only
 * the first decimal point ('1..5' -> '1.5') and truncates to maxDecimals digits after it.
 * Pair with numericFormatValidator(): this sanitizes as the user types, that blocks submission.
 * @param maxDecimals Maximum digits allowed after the decimal point. 0 = integers only.
 * @returns The sanitized string
 */
export function sanitizeDecimal(value: string | number | null | undefined, maxDecimals: number = 1): string {

  // treat a comma as a decimal separator rather than stripping it: fr-CA users write '1,5', and
  // dropping the comma would silently turn 1.5 hours into 15
  let sanitized: string = (value ?? '').toString().replace(/,/g, '.').replace(/[^0-9.]/g, '');

  // collapse repeated decimal points ('1..5' -> '1.5', '1.2.3' -> '1.23') by keeping only the first
  const firstDot: number = sanitized.indexOf('.');
  if (firstDot !== -1) {
    sanitized = sanitized.slice(0, firstDot + 1) + sanitized.slice(firstDot + 1).replace(/\./g, '');
  }

  const parts: string[] = sanitized.split('.');
  if (maxDecimals <= 0) {
    return parts[0];
  }

  // truncate excess decimal places
  if (parts.length === 2 && parts[1].length > maxDecimals) {
    return `${parts[0]}.${parts[1].slice(0, maxDecimals)}`;
  }

  return sanitized;

}

/**
 * Rejects values that are not a well-formed number.
 *
 * Angular's built-in Validators.min/max are NOT format validators: they run
 * parseFloat() and return null (valid) when the result is NaN, and when the
 * value only partially parses they validate the parsed prefix. That lets
 * strings like '1..5', '1,5' or '3-' through a min/max guarded control and on
 * into the report XML sent to DFO. Pair this validator with every min/max.
 * @param maxDecimals Maximum digits allowed after the decimal point. 0 = integers only.
 * @returns Validator function
 */
export function numericFormatValidator(maxDecimals: number = 0): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') return null;
    const value = control.value.toString().trim();
    const error = { numericFormat: { error: 'Not a valid number' } };
    if (!/^\d+(\.\d+)?$/.test(value)) return error;
    const decimals: string = value.split('.')[1];
    return decimals && decimals.length > maxDecimals ? error : null;
  }
}
